import { useMemo, useState } from 'react';

import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { KimOnboardingModal } from './kim-onboarding-modal';
import { DraftType } from '../../../../../../../../../../../backend/src/types/types';
import useGetUser from '../../../../../../../../../api/queries/useGetUser';
import { Icon } from '../../../../../../../../components/icon';
import { SubsetChip } from '../../../../../../../../components/subset-chip';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCompleteChecklistItem } from '@/api/mutations/useCompleteChecklistItem';
import { Case } from '@/api/types/backend-dto-types';
import { ChecklistStep, ExecuteKimSearch, KimMode } from '@/types';

enum KimChecklistStep {
  AskKimQuestion = 'ask-kim-question',
  UseKimExtraction = 'use-kim-extraction',
  UseKimDraft = 'use-kim-draft',
}

interface OnboardingItem {
  id: KimChecklistStep;
  title: string;
  description: string;
  icon: React.ReactNode;
  mode: KimMode;
  draftType?: DraftType | null;
  action: () => void;
}

interface KimOnboardingProps {
  executeSearch: ExecuteKimSearch;
  setMode: (mode: KimMode) => void;
  setDraftType: (draftType: DraftType | null) => void;
  setQuestionValue: (value: string) => void;
  caseData?: Case;
}

const OnboardingItemComponent = ({ item }: { item: OnboardingItem }) => {
  return (
    <motion.div
      key={item.id}
      onClick={() => item.action()}
      className="group relative cursor-pointer overflow-hidden rounded-lg border border-purple-200 bg-white p-4 pr-16 transition-all duration-300 hover:border-purple-300"
      data-testid={`kim-onboarding-item-${item.id}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.2,
        type: 'easeInOut',
      }}
    >
      <div className="group-hover:bg-opacity-80 absolute top-0 right-0 flex h-full w-12 items-center justify-center bg-purple-100">
        <span className="iconify lucide--arrow-right h-4 w-4 text-purple-600 transition-transform duration-200" />
      </div>

      <div className="flex items-center">
        <div className="relative mr-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-500 duration-200">
          <span>{item.icon}</span>
        </div>

        <div className="flex-grow">
          <h3 className="text-base font-medium">{item.title}</h3>
          <p className="mt-1 text-xs text-gray-600">{item.description}</p>
        </div>
      </div>
    </motion.div>
  );
};

export const KimOnboarding = ({
  executeSearch,
  setMode,
  setDraftType,
  setQuestionValue,
  caseData,
}: KimOnboardingProps) => {
  const { subsetId } = useParams();
  if (!subsetId) return null;

  const { data: user } = useGetUser();
  const hasCompletedKimOnboarding = user && !user.onboardingChecklistItems?.includes(ChecklistStep.UseKim);

  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [extractionModalOpen, setExtractionModalOpen] = useState(false);
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const { mutate: completeChecklistItem } = useCompleteChecklistItem();

  const onboardingItems: OnboardingItem[] = useMemo(() => {
    return [
      {
        id: KimChecklistStep.AskKimQuestion,
        title: 'Ask a question',
        description: 'Try asking Kim a question about your documents',
        icon: <Icon className="lucide--circle-help" />,
        mode: KimMode.QuestionFlow,
        action: () => {
          trackCustomEvent(TrackingEventsV2.KimOnboardingClickQuestionFlow);
          setQuestionModalOpen(true);
        },
      },
      {
        id: KimChecklistStep.UseKimExtraction,
        title: 'Extract information',
        description: 'Use Kim to extract information into a table',
        icon: <Icon className="lucide--sheet" />,
        mode: KimMode.ExtractionFlow,
        action: () => {
          trackCustomEvent(TrackingEventsV2.KimOnboardingClickExtractionFlow);
          setExtractionModalOpen(true);
        },
      },
      {
        id: KimChecklistStep.UseKimDraft,
        title: 'Draft documents',
        description: 'Use Kim to draft documents based on your case facts',
        icon: <Icon className="lucide--file-text" />,
        mode: KimMode.DraftFlow,
        draftType: DraftType.Claim,
        action: () => {
          trackCustomEvent(TrackingEventsV2.KimOnboardingClickDraftFlow);
          setDraftModalOpen(true);
        },
      },
    ];
  }, []);

  return (
    <div className="flex w-full flex-col items-center justify-center p-8">
      <div className="w-full max-w-2xl">
        <div className="mb-6 text-center">
          <h2 className="flex flex-col items-center gap-1.5 text-2xl font-semibold text-gray-800">
            <SubsetChip subsetId={subsetId} className="text-sm" />
            <span>Get started with Kim</span>
          </h2>
          <p className="mx-auto mt-4 mb-10 max-w-xl text-gray-600">
            Kim is Wexler's AI agent for gathering information and running tasks on your case. Click through the items
            below to get familiar with Kim's different workflows.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {onboardingItems.map((item) => (
            <OnboardingItemComponent key={item.id} item={item} />
          ))}
        </div>

        {!hasCompletedKimOnboarding && (
          <button
            onClick={() => completeChecklistItem({ completedChecklistItem: ChecklistStep.UseKim })}
            className="my-8 flex w-full items-center justify-center gap-2 text-center text-gray-600 duration-150 hover:text-gray-800"
          >
            Already used Kim before? Skip the onboarding
            <Icon className="lucide--arrow-right" />
          </button>
        )}
      </div>

      {/* Kim Onboarding Modals */}
      <KimOnboardingModal
        isOpen={questionModalOpen}
        setIsOpen={setQuestionModalOpen}
        flowType="question"
        caseData={caseData}
        executeSearch={executeSearch}
        setMode={setMode}
        setDraftType={setDraftType}
        setQuestionValue={setQuestionValue}
      />

      <KimOnboardingModal
        isOpen={extractionModalOpen}
        setIsOpen={setExtractionModalOpen}
        flowType="extraction"
        caseData={caseData}
        executeSearch={executeSearch}
        setMode={setMode}
        setDraftType={setDraftType}
        setQuestionValue={setQuestionValue}
      />

      <KimOnboardingModal
        isOpen={draftModalOpen}
        setIsOpen={setDraftModalOpen}
        flowType="draft"
        caseData={caseData}
        executeSearch={executeSearch}
        setMode={setMode}
        setDraftType={setDraftType}
        setQuestionValue={setQuestionValue}
      />
    </div>
  );
};
